import React, { useState, useEffect, useRef } from 'react';
import './Dashboard.css'
import { useLocation } from "react-router";
import axios from 'axios';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleUser, faBell, faMagnifyingGlass, faUsers, faUser, faRightFromBracket, faTableCellsLarge, faFolderOpen, faChartColumn, faFilePdf, faMoon, faSun } from '@fortawesome/free-solid-svg-icons';
import {Outlet} from "react-router-dom";
import { useNavigate } from "react-router";
import aeryumLogo from '../../assets/LOGO-AERYUM_blanco.png';
import { useUser } from '../../context/UserContext';
import { getImageLimit } from '../../utils/imageLimit';
import { clearAuthToken } from '../../utils/authStorage';

function Dashboard({ children }) {
    // const [filesUploaded, setFilesUploaded] = useState(false);
    // const [projectId, setProjectId] = useState();
    // const [projectName, setProjectName] = useState();
    // const [inspectionName, setInspectionName] = useState();
    // const [inspectionId, setInspectionId] = useState();
    // const [createdBy, setCreatedBy] = useState(); // si lo tenés
    // const [createdAt, setCreatedAt] = useState(); // si lo tenés
    // const [files, setFiles] = useState([]);
    const [selectedSideBarOption, setSelectedSidebarOption] = useState(null);
    const [q, setQ] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(() => {
      const stored = window.localStorage.getItem('aeryum-theme');
      if (stored === 'dark') return true;
      if (stored === 'light') return false;
      return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
    });
    const userMenuRef = useRef(null);
    const searchRef = useRef(null);
    const notificationsRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAdmin, clearUser } = useUser();
    const imageLimit = getImageLimit(user);

  const baseOptions = [
    { key: 'Modules', label: 'Modules', icon: faTableCellsLarge, route: '/' },
    { key: 'Projects', label: 'Projects', icon: faFolderOpen, route: '/projects' },
    { key: 'Reports', label: 'Reports', icon: faChartColumn, route: '/reports' },
    { key: 'Notifications', label: 'Notifications', icon: faBell, route: '/notifications' },
    { key: 'Profile', label: 'Profile', icon: faUser, route: '/profile' },
  ];

  const options = isAdmin
    ? [...baseOptions, { key: 'Users', label: 'Users', icon: faUsers, route: '/users' }]
    : baseOptions;
       // marcar activo según la ruta actual
  useEffect(() => {
    if (location.pathname === '/' || location.pathname.startsWith('/modules/')) setSelectedSidebarOption('Modules');
    else if (location.pathname.startsWith('/projects')) setSelectedSidebarOption('Projects');
    else if (location.pathname.startsWith('/reports')) setSelectedSidebarOption('Reports');
    else if (location.pathname.startsWith('/notifications')) setSelectedSidebarOption('Notifications');
    else if (location.pathname.startsWith('/profile')) setSelectedSidebarOption('Profile');
    else if (location.pathname.startsWith('/users')) setSelectedSidebarOption('Users');
    else setSelectedSidebarOption('Modules');
  }, [location.pathname]);

  const handleOptionClick = (option) => {
    setSelectedSidebarOption(option.key);
    navigate(option.route);
  };

  const handleLogout = () => {
    clearAuthToken();
    clearUser();
    navigate('/login');
  };

  useEffect(() => {
    const theme = isDarkMode ? 'dark' : 'light';
    window.localStorage.setItem('aeryum-theme', theme);
    document.documentElement.dataset.theme = theme;
  }, [isDarkMode]);

  const loadNotifications = async () => {
    try {
      setNotificationsLoading(true);
      const res = await axios.get('/api/notifications');
      setNotifications(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        setSearchLoading(true);
        const res = await axios.get('/api/search', {
          params: { q: query },
          signal: controller.signal,
        });
        setSearchResults(Array.isArray(res.data) ? res.data : []);
        setSearchOpen(true);
      } catch (err) {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          setSearchResults([]);
        }
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [q]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const clearSearch = () => {
    setQ('');
    setSearchResults([]);
    setSearchOpen(false);
  };

  const openReportPdf = async (id, title) => {
    const pdfWindow = window.open('', '_blank');
    if (pdfWindow) {
      pdfWindow.opener = null;
      pdfWindow.document.title = 'Opening report...';
      pdfWindow.document.body.innerHTML =
        '<p style="font-family: sans-serif; padding: 24px;">Opening report...</p>';
    }

    try {
      const res = await axios.get(`/api/reports/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      if (pdfWindow) {
        pdfWindow.location.replace(url);
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = `${title || 'report'}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      pdfWindow?.close();
    }
  };

  const handleSearchResult = (result) => {
    clearSearch();
    if (result.type === 'PROJECT') {
      navigate(`/projects/${result.id}`);
      return;
    }
    if (result.type === 'INSPECTION') {
      navigate(`/inspections/${result.id}`);
      return;
    }
    if (result.type === 'REPORT') {
      openReportPdf(result.id, result.title);
    }
  };

  const iconForResult = (type) => {
    if (type === 'PROJECT') return faFolderOpen;
    if (type === 'INSPECTION') return faTableCellsLarge;
    if (type === 'REPORT') return faFilePdf;
    return faMagnifyingGlass;
  };

  const unreadNotifications = notifications.filter((item) => !item.read).length;
  const notificationPreview = notifications.slice(0, 5);

  const toggleNotifications = () => {
    setNotificationsOpen((open) => {
      const next = !open;
      if (next) {
        loadNotifications();
      }
      return next;
    });
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      setNotifications((items) =>
        items.map((item) =>
          item.id === notification.id ? { ...item, read: true } : item,
        ),
      );
      axios.patch(`/api/notifications/${notification.id}/read`).catch(() => {});
    }

    setNotificationsOpen(false);
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const formatNotificationDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  };

return (
    <div className={`layout ${isDarkMode ? 'theme-dark' : 'theme-light'}`}>
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <img className="aeryumLogo" src={aeryumLogo} alt="Aeryum" />
        </div>

<ul className="nav-list">
  {options.map((option) => (
    <li
      key={option.key}
      onClick={() => handleOptionClick(option)}
      className={`nav-item ${selectedSideBarOption === option.key ? 'selected' : ''}`}
    >
      <FontAwesomeIcon icon={option.icon} className="nav-icon" />
      <span>{option.label}</span>
    </li>
  ))}
</ul>

        <div className="sidebar-footer">
          <button
            type="button"
            className="dark-toggle"
            onClick={() => setIsDarkMode((current) => !current)}
            aria-pressed={isDarkMode}
          >
            <span className="dm-label">
              <FontAwesomeIcon className="darkModeIcon" icon={isDarkMode ? faMoon : faSun} />
              {isDarkMode ? 'Dark mode' : 'Light mode'}
            </span>
            <span className="switch" aria-hidden="true">
              <input type="checkbox" checked={isDarkMode} readOnly />
              <span className="slider" />
            </span>
          </button>
        </div>

      </aside>

      {/* Main */}
      <main className="main">
        {/* TOP BAR: buscador + notificación + usuario */}
        <header className="topbar">
          <div className="searchbar" ref={searchRef}>
            <FontAwesomeIcon className="search-icon" icon={faMagnifyingGlass} />
            <input
              type="text"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              onKeyDown={(event) => {
                if (
                  event.key === 'Enter' &&
                  !searchLoading &&
                  searchResults.length > 0
                ) {
                  event.preventDefault();
                  handleSearchResult(searchResults[0]);
                }
              }}
              placeholder="Search projects, inspections or reports"
              aria-label="Search"
            />
            {searchOpen && q.trim().length >= 2 && (
              <div className="search-results">
                {searchLoading && (
                  <div className="search-state">Searching...</div>
                )}
                {!searchLoading && searchResults.length === 0 && (
                  <div className="search-state">No results found.</div>
                )}
                {!searchLoading && searchResults.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    type="button"
                    className="search-result-item"
                    onClick={() => handleSearchResult(result)}
                  >
                    <FontAwesomeIcon icon={iconForResult(result.type)} className="search-result-icon" />
                    <span className="search-result-copy">
                      <span className="search-result-title">{result.title}</span>
                      <span className="search-result-subtitle">{result.subtitle}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="topbar-right">
            <div className="notifications-menu-wrap" ref={notificationsRef}>
              <button
                className="icon-btn notification-trigger"
                aria-label="Notifications"
                onClick={toggleNotifications}
              >
                <FontAwesomeIcon icon={faBell} />
                {unreadNotifications > 0 && <span className="notification-unread-dot" />}
              </button>

              {notificationsOpen && (
                <div className="notifications-dropdown">
                  <div className="notifications-dropdown-header">
                    <span>Notifications</span>
                    {unreadNotifications > 0 && (
                      <span className="notifications-count">{unreadNotifications} unread</span>
                    )}
                  </div>

                  {notificationsLoading && (
                    <div className="notifications-dropdown-state">Loading...</div>
                  )}

                  {!notificationsLoading && notificationPreview.length === 0 && (
                    <div className="notifications-dropdown-state">No notifications yet.</div>
                  )}

                  {!notificationsLoading && notificationPreview.map((notification) => (
                    <button
                      key={notification.id}
                      type="button"
                      className={`notifications-dropdown-item ${notification.read ? '' : 'is-unread'}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <span className="notifications-item-copy">
                        <span className="notifications-item-title">{notification.title}</span>
                        <span className="notifications-item-message">{notification.message}</span>
                        <span className="notifications-item-date">
                          {formatNotificationDate(notification.createdAt)}
                        </span>
                      </span>
                      {!notification.read && <span className="notifications-item-dot" />}
                    </button>
                  ))}

                  <button
                    type="button"
                    className="notifications-view-more"
                    onClick={() => {
                      setNotificationsOpen(false);
                      navigate('/notifications');
                    }}
                  >
                    View more
                  </button>
                </div>
              )}
            </div>

            <div className="user-chip-wrap" ref={userMenuRef}>
              <div
                className="user-chip"
                role="button"
                tabIndex={0}
                onClick={() => setUserMenuOpen((v) => !v)}
              >
                <FontAwesomeIcon icon={faCircleUser} className="user-avatar" />
                <div className="user-meta">
                  <span className="user-name">
                    {user?.fullName || user?.clientName || '—'}
                  </span>
                  <span className="user-mail">{user?.email || ''}</span>
                </div>
              </div>

              {userMenuOpen && (
                <div className="user-dropdown">
                  <button
                    className="user-dropdown-item"
                    onClick={() => { setUserMenuOpen(false); navigate('/profile'); }}
                  >
                    <FontAwesomeIcon icon={faUser} />
                    <span>View profile</span>
                  </button>
                  <div className="user-dropdown-divider" />
                  <button
                    className="user-dropdown-item user-dropdown-item--danger"
                    onClick={handleLogout}
                  >
                    <FontAwesomeIcon icon={faRightFromBracket} />
                    <span>Log out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {imageLimit != null && (
          <div className="trial-banner" role="status">
            <strong>Test user</strong>
            <span>
              This account has a limit of {imageLimit} image{imageLimit === 1 ? "" : "s"} to analyze.
            </span>
          </div>
        )}

        {/* Content container (tu contenedor blanco) */}
        <div className="content">
          <div className="file-upload">
            <div className="drop-zone">
             <Outlet/>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
export default Dashboard;
